import { useState } from "react";
import useLang from "../../../hooks/useLang";
import { useParams } from "react-router-dom";
import useFeature from "./useFeature";
import useFeatureBlock from "./useFeatureBlock";
import useSpectrumBlock from "./useFeatureSpectrumBlock";
import useFeaturePredictionsBlock from "./useFeaturePredictionsBlock";
import useLinkedConclusionsBlock from "./useLinkedConclusionsBlock";
import usePatchLinkedConclusions from "./usePatchLinkedConclusions";
import useModifierModal from "../../forms/hooks/useModifierModal";
import useConfirmationModal from "../../forms/hooks/useConfirmationModal";
import {
  usePatchFeatureMutation,
  useDeleteFeatureMutation,
} from "../featuresSlice";
import useHasAccess from "../../../hooks/useHasAccess";
import useSearchParams from "../../../hooks/useSearchParams";
import paramConfig from "../../../config/paramConfig";
import prognosisHeaderStyles from "../prognosisHeader.module.css";

const useFeaturePage = () => {
  const { getParam, removeParam, setParams } = useSearchParams();
  const hasAccess = useHasAccess({ apiCall: "features.id.GET" });
  const lang = useLang();
  const [activeTab, setActiveTab] = useState("SPECTRUM");
  const { featureId } = useParams();
  const feature = useFeature({ id: featureId });
  const featureBlockItems = useFeatureBlock();
  const spectrumBlock = useSpectrumBlock();
  const predictionsBlock = useFeaturePredictionsBlock();
  const conclusionBlockItems = useLinkedConclusionsBlock();
  const { formAttribs: editFormAttribs, formActions: editFormActions } =
    useModifierModal({
      mutationHook: usePatchFeatureMutation,
      formConfigHook: useFeatureBlock,
      params: { featureId },
    });
  const { formActions: deleteFormActions } = useConfirmationModal({
    mutationHook: useDeleteFeatureMutation,
    params: { id: featureId },
    navigateOnSuccess: () => "/features/",
  });
  const { formAttribs: conclusionsFormAttribs, formActions: editConclusions } =
    useModifierModal({
      mutationHook: usePatchLinkedConclusions,
      formConfigHook: useLinkedConclusionsBlock,
    });

  const selectedSpectraType =
    getParam(paramConfig.EXAMPLE) || paramConfig.DEFAULT_EXAMPLE;
  const exampleCounts = feature?.data?.example_counts;
  const spectraTypeOptions = [
    {
      id: "positives",
      label: `${lang("spectra.positive_examples")} (${
        exampleCounts?.positive ?? 0
      })`,
    },
    {
      id: "negatives",
      label: `${lang("spectra.negative_examples")} (${
        exampleCounts?.negative ?? 0
      })`,
    },
    {
      id: "unassigned",
      label: `${lang("spectra.unassigned_examples")} (${
        exampleCounts?.unassigned ?? 0
      })`,
    },
  ];
  const handleSpectraTypeChange = (data) => {
    setParams({
      [paramConfig.EXAMPLE]: data?.id || null,
      [paramConfig.PAGE_NUMBER]: null,
    });
  };

  const bucketOrder = [
    "true_positive",
    "false_positive",
    "unassigned_positive",
    "true_negative",
    "false_negative",
    "unassigned_negative",
  ];
  const bucketOptions = bucketOrder.map((b) => ({
    id: b,
    label: `${lang(`prognosis.buckets.${b}`)} (${
      predictionsBlock.counts?.[b] ?? 0
    })`,
  }));
  const sortOptions = [
    { id: "probability", label: lang("prognosis.sort_probability") },
    { id: "last_edit_at", label: lang("prognosis.sort_last_edit_at") },
  ];
  const handleBucketChange = (data) => {
    setParams({
      [paramConfig.BUCKET]: data?.id || null,
      [paramConfig.PAGE_NUMBER]: null,
    });
  };
  const handleSortChange = (data) => {
    setParams({
      [paramConfig.SORT]: data?.id || null,
      [paramConfig.PAGE_NUMBER]: null,
    });
  };

  const pendingCount = predictionsBlock.counts?.pending || 0;
  const prognosisTitle =
    pendingCount > 0 ? (
      <span className={prognosisHeaderStyles.titleRow}>
        {lang("prognosis.title")}
        <span className={prognosisHeaderStyles.pendingBadge}>
          {`${lang("prognosis.pending")}: ${pendingCount}`}
        </span>
      </span>
    ) : (
      lang("prognosis.title")
    );

  const prognosisItems = predictionsBlock.isFetching
    ? [
        {
          id: "loading",
          label: false,
          value: lang("prognosis.loading"),
        },
      ]
    : predictionsBlock.model == null
      ? [
          {
            id: "noModel",
            label: false,
            value: lang("prognosis.no_active_model"),
          },
        ]
      : predictionsBlock.hasItems
        ? predictionsBlock.items
        : [
            {
              id: "emptyBucket",
              label: false,
              value: lang("prognosis.empty_bucket"),
            },
          ];

  const page = {
    hasAccess,
    header: {
      title: feature?.data?.name || lang("features.feature_loading"),
      lastEdit: feature?.data?.last_edit,
      buttons: [
        {
          caption: lang("common.delete"),
          type: "confirmation-modal",
          mutation: "features.id.DELETE",
          deemphasize: true,
          confirmationText: `${lang("common.delete")} ${
            feature?.data?.name
              ? '"' + feature?.data?.name + '"'
              : lang("feature.title")
          }?`,
          ...deleteFormActions,
        },
        {
          caption: lang("common.edit"),
          title: lang("features.edit_feature"),
          type: "modifier-modal",
          mutation: "features.id.PATCH",
          formAttribs: editFormAttribs,
          ...editFormActions,
        },
      ],
    },
    articles: [
      {
        items: featureBlockItems,
      },
      {
        tabs: {
          CONCLUSION: {
            id: "CONCLUSIONS",
            label: lang("conclusions.title"),
            active: activeTab === "CONCLUSIONS",
            onClick: () => {
              setActiveTab("CONCLUSIONS");
              removeParam(paramConfig.PAGE_NUMBER);
            },
          },
          SPECTRUM: {
            id: "SPECTRUM",
            label: lang("spectra.title"),
            active: activeTab === "SPECTRUM",
            onClick: () => {
              setActiveTab("SPECTRUM");
              removeParam(paramConfig.PAGE_NUMBER);
            },
          },
          PROGNOSIS: {
            id: "PROGNOSIS",
            label: lang("prognosis.title"),
            active: activeTab === "PROGNOSIS",
            onClick: () => {
              setActiveTab("PROGNOSIS");
              removeParam(paramConfig.PAGE_NUMBER);
            },
          },
          MODEL: {
            id: "MODEL",
            label: lang("model.title"),
            active: activeTab === "MODEL",
            onClick: () => {
              setActiveTab("MODEL");
              removeParam(paramConfig.PAGE_NUMBER);
            },
          },
        },
        sections: [
          {
            paging: { totalPages: spectrumBlock.pagination?.totalPages },
            header: {
              title: lang("spectra.examples"),
              buttons: [
                {
                  type: "dropdown",
                  options: spectraTypeOptions,
                  onChange: handleSpectraTypeChange,
                  value: { id: selectedSpectraType },
                  noEmpty: true,
                },
              ],
            },
            tabId: "SPECTRUM",
            items: spectrumBlock.items,
          },
          {
            header: {
              title: lang("conclusions.title"),
              buttons: {
                caption: lang("common.edit"),
                icon: "pencil",
                title: lang("features.edit_conclusions_list"),
                type: "modifier-modal",
                mutation: "features.id.conclusions.POST",
                formAttribs: conclusionsFormAttribs,
                ...editConclusions,
              },
            },
            tabId: "CONCLUSION",
            items: conclusionBlockItems,
          },
          {
            paging: { totalPages: predictionsBlock.pagination?.totalPages },
            header: {
              title: prognosisTitle,
              buttons: [
                {
                  type: "dropdown",
                  label: lang("prognosis.bucket_label"),
                  options: bucketOptions,
                  onChange: handleBucketChange,
                  value: { id: predictionsBlock.bucket },
                  noEmpty: true,
                  inputWidth: "threeQuarters",
                },
                {
                  type: "dropdown",
                  label: lang("prognosis.sort_label"),
                  options: sortOptions,
                  onChange: handleSortChange,
                  value: { id: predictionsBlock.sort },
                  noEmpty: true,
                  inputWidth: "threeQuarters",
                },
              ],
            },
            tabId: "PROGNOSIS",
            items: prognosisItems,
          },
          {
            header: {
              title: lang("model.title"),
            },
            tabId: "MODEL",
            items: [
              {
                id: "modelEvaluation",
                type: "modelEvaluation",
              },
            ],
          },
        ],
      },
    ],
  };

  return page;
};

export default useFeaturePage;
